// ────────────────────────────────────────────────────────────────────────────
// Norvig Sectors — central source of truth for sector + profession metadata
// Used by: Landing, /sektorer, JobFilters, EmployerDashboard, Onboarding
// ────────────────────────────────────────────────────────────────────────────
import { useMemo } from 'react';
import { Stethoscope, GraduationCap, Sun } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { SECTORS_EN, SECTOR_CATEGORIES_EN } from './sectors_en';

export const SECTOR_CATEGORIES = {
  public: { id: 'public', label: 'Offentlig sektor', short: 'Offentlig' },
  private: { id: 'private', label: 'Privat sektor', short: 'Privat' },
};

export const SECTORS = {
  health: {
    id: 'health',
    category: 'public',
    label: 'Helse & omsorg',
    short: 'Helse',
    icon: Stethoscope,
    tagline: 'Talenter som bryr seg.',
    description:
      'Sykepleiere, helsefagarbeidere, leger og fysioterapeuter til kommuner og sykehus over hele Norge.',
    longDescription:
      'Norges helsevesen trenger kvalifiserte hender. Vi matcher fagarbeidere med direkte autorisasjon eller godkjenningsprosess hos Helsedirektoratet, og hjelper med visum, bolig og integrering.',
    accent: '#2C4A52',
    accentSoft: '#B8CBD0',
    image: '/hero/sector-health.webp',
    professions: [
      { id: 'sykepleier', label: 'Sykepleier' },
      { id: 'helsefagarbeider', label: 'Helsefagarbeider' },
      { id: 'lege', label: 'Lege' },
      { id: 'fysioterapeut', label: 'Fysioterapeut' },
      { id: 'jordmor', label: 'Jordmor' },
      { id: 'ergoterapeut', label: 'Ergoterapeut' },
      { id: 'vernepleier', label: 'Vernepleier' },
    ],
    benefits: [
      'Autorisasjon via Helsedirektoratet',
      'Tariffavtaler og pensjonsordninger',
      'Mulighet for spesialisering',
    ],
  },
  oppvekst: {
    id: 'oppvekst',
    category: 'public',
    label: 'Oppvekst',
    short: 'Oppvekst',
    icon: GraduationCap,
    tagline: 'Pedagoger som former fremtiden.',
    description:
      'Barnehagelærere, lærere og miljøarbeidere til offentlige institusjoner.',
    longDescription:
      'Norske barnehager og skoler søker kvalifiserte pedagoger. Vi hjelper med godkjenning av utdanning, norskopplæring og overgangen til norsk pedagogisk hverdag.',
    accent: '#0A5C42',
    accentSoft: '#A8E6C8',
    image: '/hero/sector-childcare.webp',
    professions: [
      { id: 'barnehagelaerer', label: 'Barnehagelærer' },
      { id: 'laerer', label: 'Lærer' },
      { id: 'pedagog', label: 'Pedagog' },
      { id: 'miljoarbeider', label: 'Miljøarbeider' },
      { id: 'spesialpedagog', label: 'Spesialpedagog' },
    ],
    benefits: [
      'Godkjenning av utdanning fra Direktoratet',
      'Pensjonsordninger via SPK',
      'Lange ferier og familievennlige arbeidstider',
    ],
  },
  sesong: {
    id: 'sesong',
    category: 'private',
    label: 'Sesongarbeid',
    short: 'Sesong',
    icon: Sun,
    tagline: 'Sterke hender for norsk natur.',
    description:
      'Frukt-, bær- og reiselivsmedarbeidere til høysesonger over hele Norge.',
    longDescription:
      'Norge tilbyr godt betalt sesongarbeid innen frukt- og bærnæringen, gartneri og turisme. Korte engasjementer (3–6 måneder) med bolig og tariff, perfekt for å bygge nettverk og utforske landet. Hvis du trives i Norge, har du alle muligheter til å skape deg en fremtid i landet. Norske arbeidsgivere i frukt-, bær- og reiselivsnæringen trenger motiverte, engasjerte og kompetente ansatte. Din innsats blir verdsatt!',
    accent: '#5A6B5C',
    accentSoft: '#C8D4C5',
    image: '/hero/sector-sesong-hardanger.webp',
    professions: [
      { id: 'gartner', label: 'Gartner' },
      { id: 'reiselivsmedarbeider', label: 'Reiselivsmedarbeider' },
      { id: 'sesongarbeider', label: 'Sesongarbeider' },
      { id: 'baerplukker', label: 'Bærplukker' },
    ],
    benefits: [
      'Bolig som regel inkludert',
      'Tariffavtaler og overtidsbetaling',
      'Mulighet for retur hver sesong',
    ],
  },
};

export const SECTOR_LIST = Object.values(SECTORS);

export const getSectorById = (id) => SECTORS[id] || null;
export const getProfessionsBySector = (id) => SECTORS[id]?.professions || [];
export const getAllProfessions = () =>
  SECTOR_LIST.flatMap((s) =>
    s.professions.map((p) => ({ ...p, sector: s.id, sectorLabel: s.label }))
  );

// ────────────────────────────────────────────────────────────────────────────
// i18n-aware hooks. Hooks pick the active language and overlay the EN strings
// from sectors_en.js onto the base NO objects (icons + ids remain intact).
// ────────────────────────────────────────────────────────────────────────────
const overlay = (base, en) => {
  if (!en) return base;
  const merged = { ...base, ...en };
  // professions has shape [{ id, label }] — overlay by id
  if (Array.isArray(en.professions) && Array.isArray(base.professions)) {
    const byId = Object.fromEntries(en.professions.map((p) => [p.id, p.label]));
    merged.professions = base.professions.map((p) =>
      byId[p.id] ? { ...p, label: byId[p.id] } : p
    );
  }
  return merged;
};

export const useSectors = () => {
  const { language } = useLanguage();
  // Memoized: a fresh object per render caused infinite effect-loops in
  // consumers with `sectors` in dependency arrays (CreateJob "Henter yrker …").
  return useMemo(() => {
    if (language !== 'en') return SECTORS;
    return Object.fromEntries(
      Object.entries(SECTORS).map(([id, s]) => [id, overlay(s, SECTORS_EN[id])])
    );
  }, [language]);
};

export const useSectorList = () => Object.values(useSectors());

export const useSectorCategories = () => {
  const { language } = useLanguage();
  return language === 'en' ? SECTOR_CATEGORIES_EN : SECTOR_CATEGORIES;
};

export const useSectorById = (id) => useSectors()[id] || null;

